import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Send, ChevronDown, ChevronUp, Languages, Undo2 } from "lucide-react";
import { TIPOS_CORREO, CANALES } from "@/lib/constants";
import { formatHoraIngreso } from "@/lib/agenda";
import UrgenciaBadge from "@/components/bandeja/UrgenciaBadge";

/** PROMPT 71: tarjeta de Acciones con "Ver correo completo" y "Traducir" (sin depender
 * del traductor del navegador, que está bloqueado a propósito -- ver index.html). El
 * correo original NUNCA se pisa: la traducción es una vista aparte, plegable. */
export default function AccionCard({ a, onResponder, onResolver }) {
  const [expandido, setExpandido] = useState(false);
  const [traduciendo, setTraduciendo] = useState(false);
  const [traduccion, setTraduccion] = useState(a.traduccion?.idioma_destino === "es" ? a.traduccion : null);
  const [verOriginal, setVerOriginal] = useState(false);

  const traducir = async (forzar = false) => {
    setTraduciendo(true);
    try {
      const { data } = await api.post(
        `/captura/acciones/${a.id}/traducir`,
        { idioma_destino: "es" },
        { params: forzar ? { forzar: true } : {} },
      );
      setTraduccion(data);
      setVerOriginal(false);
      setExpandido(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTraduciendo(false);
    }
  };

  const mostrarTraduccion = traduccion && !verOriginal;

  return (
    <Card className="p-4" data-testid={`accion-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${
                a.tipo_correo === "sospechoso_no_reserva"
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-blue-50 text-blue-700 border-blue-200"
              }`}
            >
              {TIPOS_CORREO[a.tipo_correo] || a.tipo_correo}
            </span>
            <span className="text-[11px] text-slate-400">{CANALES[a.canal] || a.canal}</span>
            {a.parsed?.codigo_booking && (
              <span className="font-mono text-[11px] text-slate-500">{a.parsed.codigo_booking}</span>
            )}
            <span className="text-[10px] text-slate-400" data-testid={`accion-ingreso-${a.id}`}>
              Ingresó {formatHoraIngreso(a.created_at)}
            </span>
            {/* PROMPT 141: urgencia por la fecha del tour (la de la reserva vinculada o la del correo) */}
            {a.fecha_tour && <UrgenciaBadge fecha={a.fecha_tour} testid={`accion-urgencia-${a.id}`} />}
          </div>
          <p className="text-sm font-medium text-slate-900 mt-1 truncate">{a.asunto || "(sin asunto)"}</p>
          {a.parsed?.mensaje_texto && (
            <p className="text-[12px] text-slate-600 mt-1 line-clamp-2">“{a.parsed.mensaje_texto}”</p>
          )}
          {a.motivo_accion && (
            // PROMPT 114: por que este correo esta aca y que no se hizo solo (cuenta de Bókun, aviso sin sincronizar...).
            <p className="text-[12px] text-amber-700 mt-1" data-testid={`accion-motivo-${a.id}`}>⚠ {a.motivo_accion}</p>
          )}
          <p className="text-[11px] text-slate-400 mt-1">{a.remitente}</p>

          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandido((v) => !v)}
              data-testid={`accion-ver-completo-${a.id}`}
              className="gap-1.5 h-7 px-2 text-[12px] text-slate-500 hover:text-slate-800"
            >
              {expandido ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Ver correo completo
            </Button>
            {!traduccion ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => traducir(false)}
                disabled={traduciendo}
                data-testid={`accion-traducir-${a.id}`}
                className="gap-1.5 h-7 px-2 text-[12px] text-violet-600 hover:text-violet-800 hover:bg-violet-50"
              >
                {traduciendo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Languages className="h-3.5 w-3.5" />}
                Traducir
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVerOriginal((v) => !v)}
                data-testid={`accion-ver-original-${a.id}`}
                className="gap-1.5 h-7 px-2 text-[12px] text-violet-600 hover:text-violet-800 hover:bg-violet-50"
              >
                <Undo2 className="h-3.5 w-3.5" />
                {verOriginal ? "Ver traducción" : "Ver original"}
              </Button>
            )}
          </div>

          {expandido && (
            <div className="mt-2">
              {mostrarTraduccion && (
                <p className="text-[11px] text-violet-600 mb-1 flex items-center gap-1.5" data-testid={`accion-traduccion-label-${a.id}`}>
                  <Languages className="h-3.5 w-3.5" />
                  Traducción automática · del {traduccion.idioma_detectado}
                  <button
                    onClick={() => traducir(true)}
                    disabled={traduciendo}
                    className="underline text-violet-400 hover:text-violet-700 ml-1"
                    data-testid={`accion-retraducir-${a.id}`}
                  >
                    actualizar
                  </button>
                </p>
              )}
              {mostrarTraduccion && traduccion.truncado && (
                <p className="text-[11px] text-amber-600 mb-1">
                  El correo era muy largo: se tradujo solo la primera parte.
                </p>
              )}
              <pre
                className="text-[12px] leading-relaxed text-slate-700 whitespace-pre-wrap font-sans bg-slate-50 border border-slate-100 rounded px-3 py-2 max-h-56 overflow-y-auto"
                data-testid={`accion-cuerpo-completo-${a.id}`}
              >
                {mostrarTraduccion ? traduccion.cuerpo : a.cuerpo}
              </pre>
            </div>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onResponder(a)}
            data-testid={`responder-accion-${a.id}`}
            title="Responde al cliente desde la app, saliendo de la casilla de su marca."
            className="gap-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
          >
            <Send className="h-4 w-4" /> Responder
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onResolver(a.id)}
            data-testid={`resolver-accion-${a.id}`}
            title="Solo saca este correo de la lista de Acciones. No envía ningún mensaje al cliente."
            className="gap-1"
          >
            <CheckCircle2 className="h-4 w-4" /> Resolver
          </Button>
        </div>
      </div>
    </Card>
  );
}
